import { lazy, Suspense, useEffect, useState } from "react";
import welocateLogo from "../../../img/icons/welocate-bl.svg";
import useAdminHttp from "../../../hooks/use-admin-http";
import {
  setAccessToken,
  refreshAccessToken,
  logout,
} from "../../../utils/auth-token";
import "./AdminAuth.css";

// Lazy-loaded so the admin UI ships as a separate chunk, fetched only after
// authentication succeeds — it never rides along in the public bundle.
const AdminLayout = lazy(() => import("./AdminLayout"));

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const CODE_LENGTH = 6;

const AdminAuth = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  // Login is two steps: credentials first, then the one-time code that the
  // backend emails after a correct password.
  const [step, setStep] = useState("credentials");
  // "checking" while we see if the refresh cookie still holds a session.
  const [authState, setAuthState] = useState("checking");
  const { isLoading, error, clearError, sendRequest } = useAdminHttp();

  useEffect(() => {
    refreshAccessToken().then((refreshed) => {
      setAuthState(refreshed ? "loggedIn" : "loggedOut");
    });
  }, []);

  // Step 1: credentials. On success the backend has emailed a code; the
  // password is kept in memory only so "Send a new code" can repeat this step.
  const requestCode = () => {
    sendRequest(
      {
        url: `${BACKEND_URL}/login`,
        method: "POST",
        body: { username, password },
        authRetry: false,
        errorMessages: {
          401: "Invalid username or password.",
          502: "The code could not be emailed. Please try again later.",
        },
      },
      () => {
        setCode("");
        setStep("code");
      }
    );
  };

  const handleCredentialsSubmit = (e) => {
    e.preventDefault();
    requestCode();
  };

  // Step 2: the emailed code. The backend expects it as a number.
  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) return;

    sendRequest(
      {
        url: `${BACKEND_URL}/login/verify`,
        method: "POST",
        body: { email: username, code: Number(code) },
        authRetry: false,
        errorMessages: {
          401: "That code is wrong or has expired. Request a new one below.",
        },
      },
      (data) => {
        setAccessToken(data.access_token);
        setPassword("");
        setCode("");
        setStep("credentials");
        setAuthState("loggedIn");
      }
    );
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH));
  };

  const handleBackToCredentials = () => {
    clearError();
    setCode("");
    setPassword("");
    setStep("credentials");
  };

  const handleLogout = async () => {
    await logout();
    setAuthState("loggedOut");
  };

  if (authState === "checking") {
    return <div className="wla-auth" />;
  }

  if (authState === "loggedIn") {
    return (
      <Suspense fallback={<div className="wla-auth" />}>
        <AdminLayout onLogout={handleLogout} />
      </Suspense>
    );
  }

  const brand = (
    <div className="wla-auth-brand">
      <img className="wla-auth-logo" src={welocateLogo} alt="welocate" />
      <span className="wla-auth-badge">Admin</span>
    </div>
  );

  if (step === "code") {
    return (
      <div className="wla-auth">
        <form className="wla-auth-card" onSubmit={handleCodeSubmit}>
          {brand}
          <h1 className="wla-auth-title">Enter your code</h1>
          <p className="wla-auth-hint">
            We sent a six digit code to <strong>{username}</strong>. It is
            valid for 15 minutes.
          </p>
          <label className="wla-auth-field">
            <span className="wla-auth-label">Code</span>
            <input
              className="wla-auth-code"
              type="text"
              name="code"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              placeholder="123456"
              value={code}
              onChange={handleCodeChange}
              autoFocus
              required
            />
          </label>
          {error && <p className="wla-auth-error">{error}</p>}
          <button
            className="wla-auth-submit"
            type="submit"
            disabled={isLoading || code.length !== CODE_LENGTH}
          >
            {isLoading ? "Verifying…" : "Verify"}
          </button>
          <div className="wla-auth-links">
            <button
              type="button"
              className="wla-auth-link"
              onClick={requestCode}
              disabled={isLoading}
            >
              Send a new code
            </button>
            <button
              type="button"
              className="wla-auth-link"
              onClick={handleBackToCredentials}
              disabled={isLoading}
            >
              Back to login
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="wla-auth">
      <form className="wla-auth-card" onSubmit={handleCredentialsSubmit}>
        {brand}
        <h1 className="wla-auth-title">Log in</h1>
        <label className="wla-auth-field">
          <span className="wla-auth-label">Username</span>
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </label>
        <label className="wla-auth-field">
          <span className="wla-auth-label">Password</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <p className="wla-auth-error">{error}</p>}
        <button className="wla-auth-submit" type="submit" disabled={isLoading}>
          {isLoading ? "Sending code…" : "Log in"}
        </button>
      </form>
    </div>
  );
};

export default AdminAuth;
